import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { mdiOpenInNew } from '@mdi/js';
import { Icon } from '@mdi/react';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { isNil } from 'ramda';
import { isEmptyString } from 'ramda-adjunct';
import { FormattedMessage } from 'react-intl';
import { formatValue } from '../format/valueFormatters';
import { defineStyles } from '../theme';
const styles = defineStyles({
    arrayTooltip: {
        maxWidth: 300,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
});
const isLinkValue = (value) => typeof value === 'string' && value.startsWith('https://');
const isLink = (value, apiColumn) => apiColumn.dataType === 'STRING' && isLinkValue(value);
const renderStringWithLink = (value) => {
    if (isLinkValue(value)) {
        return (_jsx("span", { style: { display: 'flex', alignItems: 'center', gap: '4px' }, children: _jsx(IconButton, { component: "a", href: value, target: "_blank", rel: "noopener noreferrer", size: "small", onClick: (e) => {
                    e.stopPropagation();
                }, sx: { padding: '2px' }, "aria-label": "Open link in new window", children: _jsx(Icon, { path: mdiOpenInNew, size: 1 }) }) }));
    }
    return value;
};
const renderCellValue = (value, apiColumn) => {
    if (apiColumn.dataType === 'BOOLEAN' && typeof value === 'boolean') {
        return renderBoolean(value);
    }
    if (apiColumn.dataType === 'TIMESTAMP' && typeof value === 'string') {
        return renderTimestamp(value);
    }
    if (apiColumn.dataType === 'STRING' && typeof value === 'string') {
        return renderStringWithLink(value);
    }
    if (apiColumn.format) {
        return formatValue(value, apiColumn.format);
    }
    return value?.toString() ?? '';
};
export const renderBoolean = (value) => {
    return value === true ? (_jsx(FormattedMessage, { id: "common.yes", defaultMessage: "Yes" })) : (_jsx(FormattedMessage, { id: "common.no", defaultMessage: "No" }));
};
export const renderBooleanToJSX = (value) => {
    if (value)
        return _jsx(FormattedMessage, { id: "common.yes", defaultMessage: "Yes" });
    return _jsx(FormattedMessage, { id: "common.no", defaultMessage: "No" });
};
export const renderTimestamp = (value) => {
    try {
        const date = parseISO(value);
        return format(date, 'dd.MM.yyyy HH:mm');
    }
    catch (e) {
        console.error(`Failed to parse timestamp ${value}`);
        return '';
    }
};
const lowercaseFirstLetter = (str) => {
    if (!str)
        return str;
    return str.charAt(0).toLowerCase() + str.slice(1);
};
export const apiColumnToTableColumn = (apiColumn) => ({
    fieldName: apiColumn.fieldName,
    format: apiColumn.format,
    dataType: apiColumn.dataType,
    isArray: apiColumn.isArray,
    header: apiColumn.renderHeader?.() ?? _jsx("strong", { children: apiColumn.fieldName }),
    insertable: apiColumn.isInsertable,
    editable: apiColumn.isUiEditable,
    uploadEditable: apiColumn.isUploadEditable,
    pattern: apiColumn.pattern,
    values: apiColumn.values,
    isId: apiColumn.isId,
    isFilterOnly: apiColumn.isFilterOnly,
    fieldKey: apiColumn.fieldKey,
    bodyCellRenderer: (row) => {
        const rowKey = apiColumn.fieldKey ?? lowercaseFirstLetter(apiColumn.fieldName);
        const value = row?.[rowKey];
        if (isNil(value) || isEmptyString(value)) {
            return null;
        }
        if (!apiColumn.isArray) {
            const node = renderCellValue(value, apiColumn);
            // a link cell shows only an icon, so the tooltip carries the full URL
            return isLink(value, apiColumn) ? { node, value } : node;
        }
        const isEmptyArray = (value?.length ?? 0) === 0;
        if (isEmptyArray) {
            return null;
        }
        const renderedValues = value.map((v) => renderCellValue(v, apiColumn));
        const valueAsString = renderedValues
            .map((v) => (typeof v === 'string' ? v : typeof v === 'object' && v ? String(v) : ''))
            .join(', ');
        return (_jsx(Tooltip, { title: valueAsString, sx: styles.arrayTooltip, children: _jsx(Typography, { children: renderedValues.map((renderedValue, index) => (_jsxs("span", { children: [renderedValue, index < renderedValues.length - 1 && ', '] }, index))) }) }));
    },
});
export const rowsSortingToSortDto = (sorting) => sorting.map(({ fieldName, direction }) => ({ field: fieldName, type: direction }));
//# sourceMappingURL=helpers.js.map